// sync-variations no longer deletes a variation whose Dashpivot form stops
// coming back. It stamps claims_variations.dashpivot_removed_at instead, because
// deleting cascades into claims_hire_register and claims_hire_billing and
// destroys billing periods that were already paid.
//
// That trade is only safe if the flagged row also stops counting as money. When
// a Dashpivot form is reassigned from one project to another, the losing
// project flags its row while the gaining project inserts a fresh one for the
// same form, so without these filters the same variation would be claimed and
// counted twice.
//
// Scope note: this gates the financial and claim-selection paths only. Review
// surfaces must keep showing flagged rows, or nobody can act on the flag.

export interface DashpivotRemovableVariation {
  dashpivot_removed_at?: string | null;
}

/** True when sync found this variation missing from Dashpivot. */
export function isRemovedFromDashpivot(
  variation: DashpivotRemovableVariation,
): boolean {
  return Boolean(variation.dashpivot_removed_at);
}

/** Predicate form, for use directly in an Array.filter over a variation list. */
export function isNotRemovedFromDashpivot(
  variation: DashpivotRemovableVariation,
): boolean {
  return !isRemovedFromDashpivot(variation);
}

/** Drop every variation flagged as removed from Dashpivot. */
export function excludeRemovedFromDashpivot<
  TVariation extends DashpivotRemovableVariation,
>(variations: readonly TVariation[]): TVariation[] {
  return variations.filter(isNotRemovedFromDashpivot);
}

// ---------------------------------------------------------------------------
// Surfacing sync's removal guards to a human.
//
// sync-variations suppresses removal flagging when a fetch looks incomplete, and
// reports a project whose flagged backlog is high. Both reach the caller only in
// the response's results[], and neither invocation site used to read it, so no
// operator was ever told. A guard nobody is told about is a silent failure with
// good intentions.

/** Result type sync-variations uses when a guard suppressed removal flagging. */
export const REMOVAL_FLAGGING_SKIPPED_RESULT_TYPE = "RemovalFlaggingSkipped";

/** Result type sync-variations uses when a project's flagged backlog is high. */
export const REMOVAL_BACKLOG_HIGH_RESULT_TYPE = "RemovalBacklogHigh";

export interface SyncRemovalNotice {
  kind: "skipped" | "backlog";
  title: string;
  message: string;
  /** Stable identity of this exact condition, for transition detection. */
  signature: string;
}

function isNoticeRow(
  value: unknown,
): value is { type?: unknown; id?: unknown; docket?: unknown; reason?: unknown } {
  return typeof value === "object" && value !== null;
}

function asText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Pull the removal-guard notices out of a sync-variations response.
 *
 * Tolerant by design: the response is untyped across the network boundary, and a
 * malformed entry must never break the toast path that reports a real problem.
 */
export function extractRemovalNotices(results: unknown): SyncRemovalNotice[] {
  if (!Array.isArray(results)) return [];

  const notices: SyncRemovalNotice[] = [];

  for (const row of results) {
    if (!isNoticeRow(row)) continue;

    const type = asText(row.type);
    if (
      type !== REMOVAL_FLAGGING_SKIPPED_RESULT_TYPE &&
      type !== REMOVAL_BACKLOG_HIGH_RESULT_TYPE
    ) {
      continue;
    }

    const project = asText(row.docket) || "This project";
    const reason = asText(row.reason);
    const skipped = type === REMOVAL_FLAGGING_SKIPPED_RESULT_TYPE;

    notices.push({
      kind: skipped ? "skipped" : "backlog",
      title: skipped ? "Removal check skipped" : "Removed-variation backlog",
      message: `${project}: ${reason}`,
      // The reason carries the counts, so an unchanged condition produces an
      // unchanged signature and a worsening one produces a new signature.
      signature: `${asText(row.id) || project}|${type}|${reason}`,
    });
  }

  return notices;
}

// ---------------------------------------------------------------------------
// Transition detection.
//
// Two of the surfaced conditions are permanent by construction: all-live-missing
// stays true forever for a project whose variations were all genuinely removed,
// and a high backlog stays true because nothing retires a flag. Sync auto-fires
// on every project page visit, so toasting on the state PERSISTING would raise
// the same interruption on every visit indefinitely, which is the fastest way to
// train an operator to ignore the only signal this mechanism produces.
//
// So a notice is surfaced when its signature CHANGES, not while it holds. The
// signature carries the counts, so a worsening condition still speaks up. This
// uses browser-local storage rather than a persisted acknowledgement column,
// which means it is per browser: a different device surfaces each notice once
// more. That is the deliberate cost of not building a review surface here.

const NOTICE_MEMORY_PREFIX = "dashpivot-removal-notice";

/** The subset of the Storage API this needs, so it can be tested without a browser. */
export interface NoticeMemory {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserNoticeMemory(): NoticeMemory | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    // Private browsing and blocked-storage modes throw on access.
    return null;
  }
}

/**
 * Keep only the notices whose condition has changed since it was last surfaced.
 *
 * With no usable storage every notice passes through: failing open is right
 * here, because an extra toast is a nuisance while a missed one hides money.
 */
export function filterNewNotices(
  notices: readonly SyncRemovalNotice[],
  memory: NoticeMemory | null = browserNoticeMemory(),
): SyncRemovalNotice[] {
  if (!memory) return [...notices];

  const fresh: SyncRemovalNotice[] = [];

  for (const notice of notices) {
    const key = `${NOTICE_MEMORY_PREFIX}:${notice.kind}:${notice.signature}`;
    try {
      if (memory.getItem(key) === notice.signature) continue;
      memory.setItem(key, notice.signature);
    } catch {
      // Storage failed mid-flight: surface the notice rather than lose it.
    }
    fresh.push(notice);
  }

  return fresh;
}

// ---------------------------------------------------------------------------
// The hire chain, client mirror of
// supabase/functions/generate-progress-claim/claimable-variations.ts.
//
// Deleting a variation used to cascade through claims_hire_register into
// claims_hire_billing, which is why sync flags instead of deleting. The
// consequence is that hire SURVIVES a flagged variation, so the cross-project
// double-count arrives through hire unless the chain is taught about the flag.
//
// The modal's preview must sum the same rows the generated PDF does, so this
// mirrors the engine's rule exactly, including failing open at every step.

/** One side of a PostgREST embed, which is an object or a single-element array. */
function embeddedRelation(value: unknown): Record<string, unknown> | null {
  const candidate = Array.isArray(value) ? value[0] : value;
  return candidate && typeof candidate === "object"
    ? (candidate as Record<string, unknown>)
    : null;
}

export interface HireRowWithParentVariation {
  claims_hire_register?: unknown;
}

/**
 * False only when this hire row was created from a variation flagged as gone
 * from Dashpivot.
 *
 * claims_hire_register.source_variation_id is nullable: hire never created from
 * a variation must stay claimable, and an inner join would have silently
 * deleted that revenue. An unresolved parent is treated the same way.
 */
export function isHireRowParentRemoved(row: HireRowWithParentVariation): boolean {
  const register = embeddedRelation(row.claims_hire_register);
  if (!register) return false;
  if (!register.source_variation_id) return false;

  const parent = embeddedRelation(register.claims_variations);
  if (!parent) return false;

  return Boolean(parent.dashpivot_removed_at);
}

/** Drop hire billing rows whose parent variation is flagged as removed. */
export function excludeHireRowsWithRemovedParent<
  TRow extends HireRowWithParentVariation,
>(rows: readonly TRow[]): TRow[] {
  return rows.filter((row) => !isHireRowParentRemoved(row));
}
